<template>
  <section v-if="isVisible" :id="uid" class="mx-auto px-2 max-w-screen-xxl mt-3 mb-5" :data-magellan-target="uid">
    <template v-if="!isLoadingSpecs">
      <pdl-collapse
        icon-position="left"
        header-classes="font-heading font-bold text-lg border-b-2"
        qaid="pdp-spec-collapse"
        header-level="2"
      >
        <p qaid="spec-details-size-disclaimer">{{ $t('product.details.specs.disclaimer') }}</p>
        <component
          :is="getComponentForType(component.typeCode)"
          v-for="component of components"
          :key="component.uid"
          :component-data="component"
          :product-specs="productSpecs"
          :product="product"
        />
      </pdl-collapse>

      <!-- Trek disclaimers -->
      <p class="mt-2 mb-1">{{ disclaimerMessage }}</p>
      <p class="mb-1">{{ weightMessage }}</p>
    </template>
    <pdl-skeleton v-else is-list :list-count="6" height="15rem" />
  </section>
</template>

<script>
import {PdlSkeleton} from '@pedal/pdl-skeleton';
import {PdlCollapse} from '@pedal/pdl-collapse';
import {mapState, mapGetters, mapMutations} from 'vuex';
import {getComponentForType} from '@/modules/CmsComponentMapper.js';
import CmsApi from '@/api/cms';
import {ProductApi} from '@/api/product';

export default {
  components: {
    PdlCollapse,
    PdlSkeleton,
  },
  props: {
    uid: {
      type: String,
      default: undefined,
    },
    componentData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      getComponentForType: getComponentForType,
      components: [],
      componentDataLocal: this.componentData,
      isLoadingSpecs: true,
    };
  },
  computed: {
    ...mapState('backend', ['b2b']),
    ...mapState('productDetails', ['isBike', 'product', 'productSpecs']),
    ...mapGetters('familyDetails', ['isFDP']),
    ...mapGetters('backend', ['occUrlParams']),

    isVisible() {
      return this.isSpecItem && this.hasComponentData;
    },
    isSpecItem() {
      return this.product?.useSpecItem;
    },
    hasComponentData() {
      if (this.isFDP) {
        return !!this.componentData;
      }
      return !!this.componentDataLocal;
    },
    disclaimerMessage() {
      return this.b2b
        ? this.$t('product.specification.disclaimer.B2B')
        : this.$t('product.specification.disclaimer.B2C');
    },
    weightMessage() {
      return this.isBike
        ? this.$t('product.specification.disclaimer.weight.bikes')
        : this.$t('product.specification.disclaimer.weight.aftermarket');
    },
    componentNames() {
      return this.componentDataLocal?.cmsComponents.split(' ');
    },
    hasComponentProductData() {
      return this.componentNames?.length && this.product?.code;
    },
  },
  watch: {
    hasComponentProductData: {
      handler(value) {
        if (value) {
          this.fetchComponents(this.componentNames);
        }
      },
      immediate: true,
    },
    product: {
      handler(value) {
        if (!value?.code) return;
        if (value?.code && this.isSpecItem) {
          this.fetchSpecifications(value.code);
        }

        // Handles pdp component/container fetching
        if (this.uid && value?.code) {
          this.fetchContainerComponent(this.uid);
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations('productDetails', ['setProductSpecs']),

    async fetchComponents(components) {
      try {
        this.components = await CmsApi.getComponentsByIds(this.occUrlParams.userId, components);
      } catch (err) {
        console.error(err, 'An error occured retrieving components data.');
      }
    },
    async fetchContainerComponent(uid) {
      try {
        const pdpContainerComponent = await CmsApi.getComponentById(this.occUrlParams.userId, uid);
        this.componentDataLocal = pdpContainerComponent;
      } catch (err) {
        console.error(err, 'An error occured retrieving component data.');
      }
    },
    async fetchSpecifications(productCode) {
      try {
        this.isLoadingSpecs = true;
        const specs = await ProductApi.fetchProductSpecifications(productCode);
        this.setProductSpecs(specs);
        this.isLoadingSpecs = false;
      } catch (err) {
        console.error(err, 'An error occured retrieving product specifications.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.pdl-collapse-item) {
  .pdl-collapse-item__header {
    border-bottom: 2px solid var(--gray-100);
  }

  .pdl-collapse-item__wrap {
    border-bottom: none;
  }
}
</style>
